import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ensureGoogleMapsPlacesLoaded } from '../../utils/googleMapsLoader';

interface GooglePlacesAutocompleteInputProps {
  googleMapsApiKey: string;
  placeholder?: string;
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onResolvePlace: (payload: { details: any; prediction: { description: string; placeId: string } }) => void | Promise<void>;
}

const GooglePlacesAutocompleteInput = ({
  googleMapsApiKey,
  placeholder = 'Search for an address',
  disabled,
  value,
  onChange,
  onResolvePlace,
}: GooglePlacesAutocompleteInputProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [internalQuery, setInternalQuery] = useState('');
  const [options, setOptions] = useState<Array<{ value: string; placeId: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);

  const autocompleteServiceRef = useRef<any>(null);
  const placesServiceRef = useRef<any>(null);
  const placesContainerRef = useRef<HTMLDivElement | null>(null);
  const fetchSeqRef = useRef(0);

  const query = value !== undefined ? value : internalQuery;
  const setQuery = (next: string) => {
    onChange?.(next);
    if (value === undefined) setInternalQuery(next);
  };

  useEffect(() => {
    let alive = true;
    setLoadError(null);
    setIsLoaded(false);
    ensureGoogleMapsPlacesLoaded(googleMapsApiKey)
      .then((ok) => {
        if (alive) setIsLoaded(Boolean(ok));
      })
      .catch((err) => {
        if (!alive) return;
        setLoadError(err);
        setIsLoaded(false);
      });
    return () => {
      alive = false;
    };
  }, [googleMapsApiKey]);

  useEffect(() => {
    const google = (window as any).google;
    if (!isLoaded || !googleMapsApiKey || !google?.maps?.places) return;

    if (!autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
    }

    if (!placesContainerRef.current) {
      placesContainerRef.current = document.createElement('div');
    }

    if (!placesServiceRef.current) {
      placesServiceRef.current = new google.maps.places.PlacesService(placesContainerRef.current);
    }
  }, [isLoaded, googleMapsApiKey]);

  const effectiveDisabled = disabled || !googleMapsApiKey || Boolean(loadError) || !isLoaded;

  const help = useMemo(() => {
    if (loadError) return 'Google Maps failed to load.';
    if (!googleMapsApiKey) return 'Google Maps API key not available.';
    if (!isLoaded) return 'Loading Google Maps...';
    return null;
  }, [googleMapsApiKey, isLoaded, loadError]);

  const search = useCallback(
    (text: string) => {
      setQuery(text);
      const trimmed = String(text || '').trim();
      const svc = autocompleteServiceRef.current;
      if (effectiveDisabled || !svc || trimmed.length < 2) {
        setOptions([]);
        return;
      }

      const seq = ++fetchSeqRef.current;
      setLoading(true);
      svc.getPlacePredictions({ input: trimmed }, (predictions: any[]) => {
        if (seq !== fetchSeqRef.current) return;
        const seen = new Set<string>();
        const next = (Array.isArray(predictions) ? predictions : [])
          .map((prediction) => ({
            value: prediction.description,
            placeId: prediction.place_id,
          }))
          .filter((item) => {
            const key = String(item.value || '').trim().toLowerCase();
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        setOptions(next);
        setLoading(false);
      });
    },
    [effectiveDisabled]
  );

  const resolveDetails = useCallback(
    (placeId: string) =>
      new Promise<any>((resolve, reject) => {
        const google = (window as any).google;
        const svc = placesServiceRef.current;
        if (!svc || !placeId) return reject(new Error('PlacesService not ready'));
        svc.getDetails(
          {
            placeId,
            fields: ['address_components', 'formatted_address', 'geometry', 'place_id'],
          },
          (result: any, status: string) => {
            if (status !== google.maps.places.PlacesServiceStatus.OK || !result) {
              reject(new Error(`Place details failed: ${status || 'unknown'}`));
              return;
            }
            resolve(result);
          }
        );
      }),
    []
  );

  const handleSelect = async (option: { value: string; placeId: string }) => {
    setSelecting(true);
    try {
      setQuery(option.value);
      setOptions([]);
      const details = await resolveDetails(option.placeId);
      await onResolvePlace({ details, prediction: { description: option.value, placeId: option.placeId } });
    } finally {
      setSelecting(false);
    }
  };

  return (
    <div className="places-autocomplete">
      <input
        type="search"
        value={query}
        onChange={(event) => search(event.target.value)}
        disabled={effectiveDisabled}
        placeholder={placeholder}
        autoComplete="new-password"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
      />
      {(loading || selecting) && <span className="places-loading">Loading...</span>}
      {options.length > 0 && (
        <div className="places-options">
          {options.map((option) => (
            <button type="button" key={option.placeId} onClick={() => handleSelect(option)}>
              {option.value}
            </button>
          ))}
        </div>
      )}
      {help && <small className="places-help">{help}</small>}
    </div>
  );
};

export default GooglePlacesAutocompleteInput;
